import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faImage, faComment, faHeart, faShare, faEye, faLink, faExpand, faTimes, faInfoCircle, faBrain, faLightbulb, faExclamationTriangle, faArrowTrendUp, faCheckCircle, faExclamationCircle, faClock, faChartLine, faUsers, faThumbsUp, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import { useInstagram } from '../../context/InstagramContext';
import { useAuth } from '../../context/AuthContext';
import { creditsAPI, analysisJobsAPI, businessAuthAPI, instagramAPI } from '../../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const InstagramPage: React.FC = () => {
  // @ts-expect-error - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t, i18n } = useTranslation('dashboard');
  const { lang } = useParams<{ lang: string }>();
  const { state, fetchAccountData, fetchRecentPosts, fetchTopContent, fetchEngagementData, fetchReachData, fetchContentPerformanceData, fetchHashtagsData, fetchSentimentData, fetchProblemsData, fetchAIAnalysis } = useInstagram();
  const { business, refreshBusinessProfile } = useAuth();
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  const [modalChart, setModalChart] = useState<string | null>(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Account setup state
  const [username, setUsername] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Chart refs
  const engagementChartRef = useRef<HTMLCanvasElement | null>(null);
  const reachChartRef = useRef<HTMLCanvasElement | null>(null);
  const contentPerformanceChartRef = useRef<HTMLCanvasElement | null>(null);
  const hashtagsChartRef = useRef<HTMLCanvasElement | null>(null);
  const sentimentChartRef = useRef<HTMLCanvasElement | null>(null);

  // Modal chart refs
  const modalEngagementChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalReachChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalContentPerformanceChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalHashtagsChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalSentimentChartRef = useRef<HTMLCanvasElement | null>(null);

  const chartInstancesRef = useRef<{ [key: string]: ChartJS | null }>({});

  // Data from context
  const accountData = state.account;
  const recentPosts = state.recentPosts;
  const topContent = state.topContent;

  // Check if account exists and sync status
  const hasAccount = !!(business?.instagram?.username || business?.instagram?.isConnected);
  const syncStatus = business?.instagram?.syncStatus;
  const isPending = syncStatus === 'pending';
  const isCompleted = syncStatus === 'completed';

  // Check analysis status from business model
  const analysisStatus = business?.analysisStatus?.['instagram-analytics']?.status || 'noanalyze';
  const isAnalysisPending = analysisStatus === 'pending';
  const isAnalysisCompleted = analysisStatus === 'completed';
  const isAnalysisFailed = analysisStatus === 'failed';
  const hasAnyAnalysis = isAnalysisCompleted;

  // Fetch data on component mount only if account exists, sync is completed and analysis is completed
  useEffect(() => {
    if (hasAccount && isCompleted && isAnalysisCompleted) {
      fetchAccountData();
      fetchRecentPosts();
      fetchTopContent();
      fetchEngagementData();
      fetchReachData();
      fetchContentPerformanceData();
      fetchHashtagsData();
      fetchSentimentData();
      fetchProblemsData();
      fetchAIAnalysis('engagement');
      fetchAIAnalysis('reach');
      fetchAIAnalysis('contentPerformance');
      fetchAIAnalysis('hashtags');
      fetchAIAnalysis('sentiment');
    }
  }, [hasAccount, isCompleted, isAnalysisCompleted, fetchAccountData, fetchRecentPosts, fetchTopContent, fetchEngagementData, fetchReachData, fetchContentPerformanceData, fetchHashtagsData, fetchSentimentData, fetchProblemsData, fetchAIAnalysis]);

  // Handle username verification
  const handleVerify = async () => {
    if (!username.trim()) {
      setVerifyError(t('socialMedia.instagram.enterUsername'));
      return;
    }

    setVerifying(true);
    setVerifyError(null);
    setProfile(null);

    try {
      const response = await businessAuthAPI.getSocialMediaProfiles({ instagram: username.trim() });
      const respData = response?.data?.data || {};
      const instagramProfile = respData.instagram;

      if (instagramProfile) {
        setProfile(instagramProfile);
        setVerifyError(null);
      } else {
        setVerifyError(t('socialMedia.instagram.profileNotFound'));
        setProfile(null);
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.errors?.instagram || err?.response?.data?.message || t('socialMedia.instagram.verificationFailed');
      setVerifyError(errorMsg);
      setProfile(null);
    } finally {
      setVerifying(false);
    }
  };

  // Handle account submission
  const handleSubmit = async () => {
    if (!profile) {
      setVerifyError(t('socialMedia.instagram.verifyFirst'));
      return;
    }

    setSubmitting(true);
    setVerifyError(null);

    try {
      await businessAuthAPI.addSocialMediaAccounts({ instagram: username.trim() });
      await refreshBusinessProfile();
      
      // Start sync in background
      try {
        void businessAuthAPI.syncSocialMediaData();
      } catch (_e) {
        // Ignore sync errors
      }
    } catch (err: any) {
      setVerifyError(err?.response?.data?.message || t('socialMedia.instagram.accountAddError'));
    } finally {
      setSubmitting(false);
    }
  };

  const customerFeedback = [
    {
      id: 1,
      username: '@marketing_pro',
      comment: 'Harika bir platform! AI önerileri çok işime yarıyor.',
      sentiment: 'positive',
      likes: 12,
      postedAt: '1 saat önce'
    },
    {
      id: 2,
      username: '@social_media_guru',
      comment: 'Analiz raporları çok detaylı, teşekkürler!',
      sentiment: 'positive',
      likes: 8,
      postedAt: '3 saat önce'
    },
    {
      id: 3,
      username: '@content_creator',
      comment: 'Daha fazla Instagram özelliği ekleyebilir misiniz?',
      sentiment: 'suggestion',
      likes: 5,
      postedAt: '5 saat önce'
    },
    {
      id: 4,
      username: '@business_owner',
      comment: 'Bazen yavaş yükleniyor, düzeltilmesi gerekiyor.',
      sentiment: 'negative',
      likes: 3,
      postedAt: '1 gün önce'
    }
  ];



  // Memoized tooltip handlers
  const handleTooltipEnter = useCallback((tooltipId: string) => {
    setHoveredTooltip(tooltipId);
  }, []);

  const handleTooltipLeave = useCallback(() => {
    setHoveredTooltip(null);
  }, []);

  // Simple Tooltip component
  const Tooltip: React.FC<{ id: string; content: string; children: React.ReactNode }> = ({ id, content, children }) => (
    <div className="relative inline-block">
      {children}
      {hoveredTooltip === id && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 w-64 text-left">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );

  // AI Analysis component
  const AIAnalysis: React.FC<{ chartType: string }> = ({ chartType }) => {
    const analysis = state.aiAnalysis[chartType];
    const isLoading = state.loading.aiAnalysis[chartType];
    const error = state.error.aiAnalysis[chartType];
    
    if (isLoading) {
      return (
        <div className="mt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-white/70">{t('socialMedia.common.aiAnalysisLoading')}</div>
          </div>
        </div>
      );
    }
    
    if (error === 'PREPARING') {
      return (
        <div className="mt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4">
              <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
            <p className="text-white/70 text-lg font-medium mb-2">{t('socialMedia.common.preparingAiAnalysis')}</p>
            <p className="text-white/50 text-sm">{t('socialMedia.common.pleaseWait')}</p>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="mt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-red-400">{t('socialMedia.common.aiAnalysisError')} {error}</div>
          </div>
        </div>
      );
    }
    
    if (!analysis) return null;

    return (
      <div className="mt-6 space-y-3">
        <div className="flex items-center space-x-2 mb-4">
          <FontAwesomeIcon icon={faBrain} className="text-purple-400 text-lg" />
          <h4 className="text-lg font-semibold text-white">{t('socialMedia.common.aiAnalysisTitle')}</h4>
        </div>
        <div className="space-y-3">
          {analysis.insights.map((insight, index) => (
            <div key={index} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  insight.type === 'insight' ? 'bg-blue-500/20' :
                  insight.type === 'prediction' ? 'bg-green-500/20' :
                  insight.type === 'recommendation' ? 'bg-purple-500/20' :
                  insight.type === 'warning' ? 'bg-yellow-500/20' :
                  'bg-gray-500/20'
                }`}>
                  <FontAwesomeIcon 
                    icon={
                      insight.type === 'insight' ? faLightbulb :
                      insight.type === 'prediction' ? faArrowTrendUp :
                      insight.type === 'recommendation' ? faBrain :
                      insight.type === 'warning' ? faExclamationTriangle :
                      faInfoCircle
                    } 
                    className={`text-sm ${
                      insight.type === 'insight' ? 'text-blue-400' :
                      insight.type === 'prediction' ? 'text-green-400' :
                      insight.type === 'recommendation' ? 'text-purple-400' :
                      insight.type === 'warning' ? 'text-yellow-400' :
                      'text-gray-400'
                    }`} 
                  />
                </div>
                <div className="flex-1">
                  <h5 className="text-white font-medium mb-1">{insight.title}</h5>
                  <p className="text-white/70 text-sm leading-relaxed">{insight.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Engagement Chart
  useEffect(() => {
    if (engagementChartRef.current && state.engagement) {
      const ctx = engagementChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.engagement) {
          chartInstancesRef.current.engagement.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'line',
          data: state.engagement,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.engagement = chart;
      }
    }
  }, [state.engagement]);

  // Reach Chart
  useEffect(() => {
    if (reachChartRef.current) {
      const ctx = reachChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.reach) {
          chartInstancesRef.current.reach.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'bar',
          data: {
            labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
            datasets: [{
              label: 'Reach',
              data: [7200, 7800, 8200, 8500, 8800, 8500],
              backgroundColor: 'rgba(228, 64, 95, 0.8)',
              borderColor: '#E4405F',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.reach = chart;
      }
    }
  }, [state.reach]);

  // Content Performance Chart
  useEffect(() => {
    if (contentPerformanceChartRef.current && state.contentPerformance) {
      const ctx = contentPerformanceChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.contentPerformance) {
          chartInstancesRef.current.contentPerformance.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'doughnut',
          data: state.contentPerformance,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  },
                  padding: 20
                }
              }
            }
          }
        });

        chartInstancesRef.current.contentPerformance = chart;
      }
    }
  }, [state.contentPerformance]);

  // Sentiment Chart
  useEffect(() => {
    if (sentimentChartRef.current && state.sentiment) {
      const ctx = sentimentChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.sentiment) {
          chartInstancesRef.current.sentiment.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'bar',
          data: state.sentiment,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.sentiment = chart;
      }
    }
  }, [state.sentiment]);

  // Hashtags Chart
  useEffect(() => {
    if (hashtagsChartRef.current && state.hashtags) {
      const ctx = hashtagsChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.hashtags) {
          chartInstancesRef.current.hashtags.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'doughnut',
          data: state.hashtags,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  },
                  padding: 20
                }
              }
            }
          }
        });

        chartInstancesRef.current.hashtags = chart;
      }
    }
  }, [state.hashtags]);

  // Modal chart creation functions
  const createModalChart = (chartType: string, ref: React.RefObject<HTMLCanvasElement | null>) => {
    if (!ref.current) return;

    const ctx = ref.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstancesRef.current[`modal_${chartType}`]) {
      chartInstancesRef.current[`modal_${chartType}`]?.destroy();
    }

    let chart: ChartJS;

    switch (chartType) {
      case 'engagement':
        chart = new ChartJS(ctx, {
          type: 'line',
          data: state.engagement || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });
        break;
      case 'reach':
        chart = new ChartJS(ctx, {
          type: 'bar',
          data: state.reach || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });
        break;
      case 'contentPerformance':
        chart = new ChartJS(ctx, {
          type: 'doughnut',
          data: state.contentPerformance || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  },
                  padding: 20
                }
              }
            }
          }
        });
        break;
      case 'hashtags':
        chart = new ChartJS(ctx, {
          type: 'doughnut',
          data: state.hashtags || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  },
                  padding: 20
                }
              }
            }
          }
        });
        break;
      case 'sentiment':
        chart = new ChartJS(ctx, {
          type: 'bar',
          data: state.sentiment || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });
        break;
      default:
        return;
    }

    chartInstancesRef.current[`modal_${chartType}`] = chart;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(chartInstancesRef.current).forEach(chart => {
        if (chart) {
          chart.destroy();
        }
      });
    };
  }, []);

  // Show account setup if no account exists
  if (!hasAccount) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <FontAwesomeIcon icon={faInstagram} className="text-6xl text-pink-500 mb-6" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('socialMedia.instagram.connectAccount')}</h1>
            <p className="text-white/70 text-sm sm:text-base mb-8">
              {t('socialMedia.connectDescription')}
            </p>
          </div>

          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                {t('socialMedia.instagram.username')}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faInstagram} className="text-white/40 text-sm" />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setVerifyError(null);
                      setProfile(null);
                    }}
                    placeholder={t('socialMedia.engagement.platformPlaceholders.instagram')}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                    disabled={verifying || submitting}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !verifying && !submitting) {
                        handleVerify();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={handleVerify}
                  disabled={!username.trim() || verifying || submitting}
                  className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifying ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  ) : (
                    t('socialMedia.common.verify')
                  )}
                </button>
              </div>
            </div>

            {verifyError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-red-400 text-sm">{verifyError}</p>
              </div>
            )}

            {profile && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-2">
                  {profile.profileImageUrl && (
                    <img
                      src={profile.profileImageUrl}
                      alt="Profile"
                      className="w-12 h-12 rounded-full"
                    />
                  )}
                  <div>
                    <p className="text-white font-medium">{profile.displayName || profile.username}</p>
                    <p className="text-white/70 text-sm">@{profile.username}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-sm text-white/70 mt-3">
                  {profile.followerCount !== undefined && (
                    <span>{profile.followerCount.toLocaleString()} {t('socialMedia.instagram.followers')}</span>
                  )}
                  {profile.mediaCount !== undefined && (
                    <span>{profile.mediaCount} {t('socialMedia.instagram.posts')}</span>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={!profile || submitting}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-600 px-6 py-3 rounded-xl text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  {t('socialMedia.instagram.adding')}
                </span>
              ) : (
                t('socialMedia.instagram.addAndSync')
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show pending message if sync is pending
  if (isPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faInstagram} className="text-4xl sm:text-5xl text-pink-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.common.dataPreparing')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            Instagram {t('socialMedia.common.dataSyncing')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.common.autoUpdate')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show analysis pending screen if sync completed but analysis is pending
  if (isCompleted && isAnalysisPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faInstagram} className="text-4xl sm:text-5xl text-pink-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.common.analysisInProgress')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            Instagram {t('socialMedia.common.analysisProgress')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.common.analysisComplete')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show analysis start screen if sync completed but no analysis exists
  if (isCompleted && !isAnalysisCompleted && !isAnalysisPending) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center mb-6 mx-auto">
                <FontAwesomeIcon icon={faBrain} className="text-3xl sm:text-4xl text-white" />
              </div>
              <FontAwesomeIcon icon={faInstagram} className="text-4xl sm:text-5xl text-pink-500 mb-4" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.common.startAiAnalysis')}</h1>
              <p className="text-white/70 text-sm sm:text-base mb-6">
                {t('socialMedia.analytics.analyticsReady')}
              </p>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faLightbulb} className="text-yellow-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.common.aiAnalysisFeatures')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.detailedPerformance')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faChartLine} className="text-blue-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.common.chartsAndReports')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.chartsAndReportsDescription')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faBrain} className="text-purple-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.common.smartRecommendations')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.smartRecommendationsDescription')}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={async () => {
                  setAiModalOpen(true);
                  setEstimatedCredits(null);
                  // Set default dates (last 30 days)
                  const today = new Date();
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  setEndDate(today.toISOString().split('T')[0]);
                  setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                }}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-600 px-6 py-4 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity text-base sm:text-lg"
              >
                <FontAwesomeIcon icon={faBrain} className="mr-2" />
                {t('socialMedia.common.startAiAnalysis')}
              </button>
            </div>
          </div>
        </div>

        {/* AI Analysis Modal */}
        {aiModalOpen && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
            <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-white/10">
                <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.common.aiAnalysisTitle')}</h3>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.startDate') || 'Başlangıç Tarihi'}</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.endDate') || 'Bitiş Tarihi'}</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                </div>
                <div className="text-white/80">
                  {t('socialMedia.common.estimatedCredits')}
                  <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                    {estimateLoading ? (
                      <span className="flex items-center">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        {t('socialMedia.common.calculating')}
                      </span>
                    ) : (
                      estimatedCredits !== null 
                        ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                        : '—'
                    )}
                  </div>
                </div>
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!startDate || !endDate}
                  onClick={async () => {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    setEstimatedCredits(null);
                    try {
                      const res = await creditsAPI.estimate('instagram', startDate, endDate);
                      const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                      setEstimatedCredits(typeof value === 'number' ? value : null);
                    } finally {
                      setEstimateLoading(false);
                    }
                  }}
                >
                  {t('socialMedia.common.calculateCredits') || 'Kredi Hesapla'}
                </button>
                <button
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                  onClick={async () => {
                    try {
                      if (!startDate || !endDate) return;
                      setEstimateLoading(true);
                      await analysisJobsAPI.start('instagram', startDate, endDate);
                    } finally {
                      setEstimateLoading(false);
                      setAiModalOpen(false);
                    }
                  }}
                >
                  {t('socialMedia.common.spend')}
                </button>
                <button
                  className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                  onClick={() => {
                    setAiModalOpen(false);
                    setStartDate('');
                    setEndDate('');
                    setEstimatedCredits(null);
                  }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 lg:space-y-8 px-2 md:px-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <div className="flex items-center space-x-2 md:space-x-3 lg:space-x-4">
          <FontAwesomeIcon icon={faInstagram} className="text-2xl sm:text-3xl md:text-4xl text-pink-500" />
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{t('socialMedia.instagram.title')}</h1>
            <p className="text-white/70 text-xs sm:text-sm md:text-base">{t('socialMedia.instagram.subtitle')}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 md:gap-4">
          {/* Sync Status Pill (Instagram specific) */}
          {business?.instagram && (
            <div className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm ${
              business.instagram.syncStatus === 'completed'
                ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                : business.instagram.syncStatus === 'pending'
                  ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                  : business.instagram.syncStatus === 'failed'
                    ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                    : 'bg-white/10 text-white/70 border border-white/20'
            }`}>
              <span className="hidden sm:inline">
                {business.instagram.syncStatus === 'completed' && t('socialMedia.instagram.syncCompleted')}
                {business.instagram.syncStatus === 'pending' && t('socialMedia.instagram.syncPending')}
                {business.instagram.syncStatus === 'failed' && (business.instagram.syncError ? `${t('socialMedia.instagram.syncError')} ${business.instagram.syncError}` : t('socialMedia.instagram.syncFailed'))}
                {!business.instagram.syncStatus && t('socialMedia.instagram.syncStatusUnknown')}
              </span>
              <span className="sm:hidden">
                {business.instagram.syncStatus === 'completed' && t('socialMedia.instagram.completed')}
                {business.instagram.syncStatus === 'pending' && t('socialMedia.instagram.pending')}
                {business.instagram.syncStatus === 'failed' && t('socialMedia.instagram.error')}
                {!business.instagram.syncStatus && t('socialMedia.instagram.unknown')}
              </span>
              {business.instagram.syncAt && (
                <span className="ml-2 opacity-75 hidden md:inline">
                  • {new Date(business.instagram.syncAt).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}
                </span>
              )}
            </div>
          )}
          <button
            onClick={async () => {
              setAiModalOpen(true);
              setEstimatedCredits(null);
              // Set default dates (last 30 days)
              const today = new Date();
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(today.getDate() - 30);
              setEndDate(today.toISOString().split('T')[0]);
              setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
            }}
            disabled={business?.instagram?.syncStatus !== 'completed'}
            className="px-3 md:px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-xs md:text-sm md:hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="hidden sm:inline">{t('socialMedia.common.startAiAnalysis')}</span>
            <span className="sm:hidden">{t('socialMedia.common.aiAnalysis')}</span>
          </button>
          <button className="bg-gradient-to-r from-pink-500 to-purple-600 px-4 md:px-6 py-2 rounded-xl text-white font-medium hover:opacity-90 transition-opacity text-xs md:text-sm">
            <FontAwesomeIcon icon={faLink} className="mr-1 md:mr-2" />
            <span className="hidden sm:inline">{t('socialMedia.instagram.refreshAccount')}</span>
            <span className="sm:hidden">{t('socialMedia.instagram.refresh')}</span>
          </button>
        </div>
      </div>

      {/* Account Info */}
      {state.loading.account ? (
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="animate-pulse">
            <div className="flex items-center space-x-6">
              <div className="w-20 h-20 bg-gray-600 rounded-full"></div>
              <div className="flex-1">
                <div className="h-6 bg-gray-600 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-600 rounded w-2/3 mb-4"></div>
                <div className="flex space-x-6">
                  <div className="h-4 bg-gray-600 rounded w-24"></div>
                  <div className="h-4 bg-gray-600 rounded w-20"></div>
                  <div className="h-4 bg-gray-600 rounded w-16"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : state.error.account ? (
        <div className="backdrop-blur-xl bg-red-500/10 border border-red-500/20 rounded-2xl p-6">
          <p className="text-red-400">{state.error.account}</p>
        </div>
      ) : accountData && (
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
          <div className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-4 md:space-x-6">
            <img 
              src={accountData.profilePicture} 
              alt="Profile" 
              className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full border-2 border-pink-500 mx-auto sm:mx-0"
              crossOrigin="anonymous"
              referrerPolicy="no-referrer"
            />
            <div className="flex-1 w-full">
              <div className="flex items-center justify-center sm:justify-start space-x-2 mb-1">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{accountData.username}</h2>
                {accountData.isVerified && (
                  <FontAwesomeIcon icon={faCheckCircle} className="text-blue-400 text-sm sm:text-base" />
                )}
              </div>
              {accountData.fullName && (
                <p className="text-white/80 text-xs sm:text-sm mb-2 sm:mb-3 text-center sm:text-left">{accountData.fullName}</p>
              )}
              {accountData.bio && (
                <p className="text-white/70 mb-2 sm:mb-3 text-xs sm:text-sm text-center sm:text-left">{accountData.bio}</p>
              )}
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3 md:gap-6 text-white/70 mb-2 sm:mb-3 text-xs sm:text-sm">
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faUsers} />
                  <span>{(accountData.followers ?? 0).toLocaleString()} {t('socialMedia.instagram.followers')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faUser} />
                  <span>{(accountData.following ?? 0).toLocaleString()} {t('socialMedia.instagram.following')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faImage} />
                  <span>{accountData.posts} {t('socialMedia.instagram.posts')}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {accountData.isBusinessAccount && (
                  <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">{t('socialMedia.instagram.accountInfo.businessAccount')}</span>
                )}
                {accountData.isPrivate && (
                  <span className="px-2 py-1 text-xs rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">{t('socialMedia.instagram.accountInfo.private')}</span>
                )}
                {accountData.hasChannel && (
                  <span className="px-2 py-1 text-xs rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">{t('socialMedia.instagram.accountInfo.hasChannel')}</span>
                )}
                {typeof accountData.highlightReelCount === 'number' && (
                  <span className="px-2 py-1 text-xs rounded bg-pink-500/20 text-pink-300 border border-pink-500/30">{t('socialMedia.instagram.accountInfo.highlight')} {accountData.highlightReelCount}</span>
                )}
                {typeof accountData.igtvVideoCount === 'number' && (
                  <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-300 border border-green-500/30">{t('socialMedia.instagram.accountInfo.igtv')} {accountData.igtvVideoCount}</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {accountData.website && (
                  <a
                    href={accountData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-300 hover:text-blue-200 text-sm underline underline-offset-4"
                  >
                    {t('socialMedia.instagram.accountInfo.website')}
                  </a>
                )}
                {Array.isArray(accountData.externalUrls) && accountData.externalUrls.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {accountData.externalUrls.slice(0, 3).map((u, i) => (
                      <a
                        key={`${u}-${i}`}
                        href={u}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 py-1 text-xs rounded bg-white/10 hover:bg-white/20 text-white/80 transition-colors border border-white/10"
                      >
                        {t('socialMedia.instagram.accountInfo.link')} {i + 1}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6">
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-xs md:text-sm">{t('socialMedia.instagram.metrics.engagementRate')}</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white">%15.2</p>
              <p className="text-green-400 text-xs md:text-sm">+2.1% {t('socialMedia.instagram.metrics.thisMonth')}</p>
            </div>
            <FontAwesomeIcon icon={faHeart} className="text-xl sm:text-2xl md:text-3xl text-pink-400" />
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-xs md:text-sm">{t('socialMedia.instagram.metrics.avgReach')}</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white">8.5K</p>
              <p className="text-green-400 text-xs md:text-sm">+12% {t('socialMedia.instagram.metrics.thisMonth')}</p>
            </div>
            <FontAwesomeIcon icon={faEye} className="text-xl sm:text-2xl md:text-3xl text-blue-400" />
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-xs md:text-sm">{t('socialMedia.common.totalLikes')}</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white">45.2K</p>
              <p className="text-green-400 text-xs md:text-sm">+8% {t('socialMedia.instagram.metrics.thisMonth')}</p>
            </div>
            <FontAwesomeIcon icon={faThumbsUp} className="text-xl sm:text-2xl md:text-3xl text-green-400" />
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-xs md:text-sm">{t('socialMedia.common.commentCount')}</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white">3.1K</p>
              <p className="text-green-400 text-xs md:text-sm">+15% {t('socialMedia.instagram.metrics.thisMonth')}</p>
            </div>
            <FontAwesomeIcon icon={faComment} className="text-xl sm:text-2xl md:text-3xl text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
        {/* Engagement Chart */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 lg:mb-6 gap-2">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.common.engagementTrend')}</h3>
              <Tooltip id="engagement-tooltip" content={t('socialMedia.common.engagementRateTooltip')}>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                  onMouseEnter={() => handleTooltipEnter('engagement-tooltip')}
                  onMouseLeave={handleTooltipLeave}
                />
              </Tooltip>
            </div>
            <button
              onClick={() => {
                setModalChart('engagement');
                setTimeout(() => createModalChart('engagement', modalEngagementChartRef), 100);
              }}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
            </button>
          </div>
          <div className="h-48 sm:h-56 md:h-64">
            {state.loading.engagement ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-pink-500"></div>
              </div>
            ) : state.error.engagement === 'PREPARING' ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-2">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
                <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.common.preparingEngagement')}</p>
                <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.common.pleaseWait')}</p>
              </div>
            ) : state.error.engagement ? (
              <div className="flex items-center justify-center h-full px-2">
                <p className="text-red-400 text-xs sm:text-sm">{state.error.engagement}</p>
              </div>
            ) : (
              <canvas ref={engagementChartRef}></canvas>
            )}
          </div>
          <AIAnalysis chartType="engagement" />
        </div>

        {/* Reach Chart */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 lg:mb-6 gap-2">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.common.reachAnalysis')}</h3>
              <Tooltip id="reach-tooltip" content={t('socialMedia.instagram.charts.reachTooltip')}>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                  onMouseEnter={() => handleTooltipEnter('reach-tooltip')}
                  onMouseLeave={handleTooltipLeave}
                />
              </Tooltip>
            </div>
            <button
              onClick={() => {
                setModalChart('reach');
                setTimeout(() => createModalChart('reach', modalReachChartRef), 100);
              }}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
            </button>
          </div>
          <div className="h-48 sm:h-56 md:h-64">
            {state.loading.reach ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-pink-500"></div>
              </div>
            ) : state.error.reach === 'PREPARING' ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-2">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
                <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.common.preparingReach')}</p>
                <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.common.pleaseWait')}</p>
              </div>
            ) : state.error.reach ? (
              <div className="flex items-center justify-center h-full px-2">
                <p className="text-red-400 text-xs sm:text-sm">{state.error.reach}</p>
              </div>
            ) : (
              <canvas ref={reachChartRef}></canvas>
            )}
          </div>
          <AIAnalysis chartType="reach" />
        </div>

        {/* Content Performance Chart */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 lg:mb-6 gap-2">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.common.contentPerformanceAnalysis')}</h3>
              <Tooltip id="content-tooltip" content={t('socialMedia.instagram.charts.contentTooltip')}>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                  onMouseEnter={() => handleTooltipEnter('content-tooltip')}
                  onMouseLeave={handleTooltipLeave}
                />
              </Tooltip>
            </div>
            <button
              onClick={() => {
                setModalChart('contentPerformance');
                setTimeout(() => createModalChart('contentPerformance', modalContentPerformanceChartRef), 100);
              }}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
            </button>
          </div>
          <div className="h-48 sm:h-56 md:h-64">
            {state.loading.contentPerformance ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-pink-500"></div>
              </div>
            ) : state.error.contentPerformance === 'PREPARING' ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-2">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
                <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.common.preparingContentPerformance')}</p>
                <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.common.pleaseWait')}</p>
              </div>
            ) : state.error.contentPerformance ? (
              <div className="flex items-center justify-center h-full px-2">
                <p className="text-red-400 text-xs sm:text-sm">{state.error.contentPerformance}</p>
              </div>
            ) : (
              <canvas ref={contentPerformanceChartRef}></canvas>
            )}
          </div>
          <AIAnalysis chartType="contentPerformance" />
        </div>

        {/* Hashtags Chart */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 lg:mb-6 gap-2">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.common.hashtagAnalysis')}</h3>
              <Tooltip id="hashtags-tooltip" content={t('socialMedia.instagram.charts.hashtagsTooltip')}>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                  onMouseEnter={() => handleTooltipEnter('hashtags-tooltip')}
                  onMouseLeave={handleTooltipLeave}
                />
              </Tooltip>
            </div>
            <button
              onClick={() => {
                setModalChart('hashtags');
                setTimeout(() => createModalChart('hashtags', modalHashtagsChartRef), 100);
              }}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
            </button>
          </div>
          {state.loading.hashtags ? (
            <div className="h-48 sm:h-56 md:h-64 flex items-center justify-center">
              <div className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.loading')}</div>
            </div>
          ) : state.error.hashtags === 'PREPARING' ? (
            <div className="h-48 sm:h-56 md:h-64 flex flex-col items-center justify-center text-center px-2">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4">
                <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
              </div>
              <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.common.preparingHashtag')}</p>
              <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.common.pleaseWait')}</p>
            </div>
          ) : state.error.hashtags ? (
            <div className="h-48 sm:h-56 md:h-64 flex items-center justify-center px-2">
              <p className="text-red-400 text-xs sm:text-sm">{t('socialMedia.common.error')} {state.error.hashtags}</p>
            </div>
          ) : (
            <div className="h-48 sm:h-56 md:h-64">
              <canvas ref={hashtagsChartRef}></canvas>
            </div>
          )}
          <AIAnalysis chartType="hashtags" />
        </div>

        {/* Sentiment Chart */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 lg:mb-6 gap-2">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.common.sentimentAnalysis')}</h3>
              <Tooltip id="sentiment-tooltip" content={t('socialMedia.instagram.charts.sentimentTooltip')}>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                  onMouseEnter={() => handleTooltipEnter('sentiment-tooltip')}
                  onMouseLeave={handleTooltipLeave}
                />
              </Tooltip>
            </div>
            <button
              onClick={() => {
                setModalChart('sentiment');
                setTimeout(() => createModalChart('sentiment', modalSentimentChartRef), 100);
              }}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
            </button>
          </div>
          <div className="h-48 sm:h-56 md:h-64">
            {state.loading.sentiment ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-pink-500"></div>
              </div>
            ) : state.error.sentiment === 'PREPARING' ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-2">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-3 sm:mb-4">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
                <p className="text-white/70 text-sm sm:text-base md:text-lg font-medium mb-1 sm:mb-2">{t('socialMedia.common.preparingSentiment')}</p>
                <p className="text-white/50 text-xs sm:text-sm">{t('socialMedia.common.pleaseWait')}</p>
              </div>
            ) : state.error.sentiment ? (
              <div className="flex items-center justify-center h-full px-2">
                <p className="text-red-400 text-xs sm:text-sm">{state.error.sentiment}</p>
              </div>
            ) : (
              <canvas ref={sentimentChartRef}></canvas>
            )}
          </div>
          <AIAnalysis chartType="sentiment" />
        </div>
      </div>

      {/* Problems Section */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-6 gap-2">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">{t('socialMedia.instagram.problems.title')}</h3>
            <Tooltip id="problems-tooltip" content={t('socialMedia.instagram.problems.tooltip')}>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                onMouseEnter={() => handleTooltipEnter('problems-tooltip')}
                onMouseLeave={handleTooltipLeave}
              />
            </Tooltip>
          </div>
        </div>
        
        {state.loading.problems ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-white/70">{t('socialMedia.instagram.problems.loading')}</div>
          </div>
        ) : state.error.problems === 'PREPARING' ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-white/70">{t('socialMedia.instagram.problems.preparing')}</div>
          </div>
        ) : state.error.problems ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-400">{t('socialMedia.instagram.problems.error')}</div>
          </div>
        ) : state.problems && state.problems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 lg:gap-6">
            {state.problems.map((problem, index) => (
              <div key={index} className="backdrop-blur-xl bg-red-500/10 border border-red-500/20 rounded-lg md:rounded-xl p-3 md:p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faExclamationCircle} className="text-red-400" />
                    <h4 className="text-white font-semibold">{problem.title}</h4>
                  </div>
                  <div className="bg-red-500/20 text-red-400 px-2 py-1 rounded-full text-sm font-medium">
                    {problem.count} {t('socialMedia.instagram.problems.times')}
                  </div>
                </div>
                <div className="bg-red-500/10 rounded-lg p-3">
                  <p className="text-white/80 text-sm">
                    <span className="font-medium text-red-400">{t('socialMedia.instagram.problems.recommendation')}</span> {problem.recommendation}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4 mx-auto">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-400 text-2xl" />
              </div>
              <p className="text-white/70 text-lg font-medium mb-2">{t('socialMedia.instagram.problems.noProblems')}</p>
              <p className="text-white/50 text-sm">{t('socialMedia.instagram.problems.noProblemsDescription')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Recent Posts */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
        <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-3 md:mb-4 lg:mb-6">{t('socialMedia.instagram.recentPosts.title')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
          {(Array.isArray(recentPosts) ? recentPosts : []).map((post) => (
            <div key={post.id} className="bg-white/5 rounded-xl p-4">
              {post.videoUrl ? (
                <video 
                  src={post.videoUrl} 
                  controls
                  className="w-full h-auto rounded-lg mb-4 bg-black/20"
                  crossOrigin="anonymous"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img 
                  src={post.image} 
                  alt="Post" 
                  className="w-full h-auto object-contain rounded-lg mb-4 bg-black/20"
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                />
              )}
              <p className="text-white/80 text-sm mb-4 line-clamp-2">{post.caption}</p>
              <div className="flex items-center justify-between text-white/60 text-sm">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <FontAwesomeIcon icon={faHeart} />
                    <span>{post.likes}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FontAwesomeIcon icon={faComment} />
                    <span>{post.comments}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FontAwesomeIcon icon={faShare} />
                    <span>{post.shares}</span>
                  </div>
                </div>
                <span>{post.postedAt}</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-white/70">{t('socialMedia.instagram.recentPosts.reach')} {(post.reach ?? 0).toLocaleString()}</span>
                <span className="text-green-400">{t('socialMedia.instagram.recentPosts.engagement')} %{post.engagement}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Content */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6">
        <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-3 md:mb-4 lg:mb-6">{t('socialMedia.common.mostLikedContent')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
          {(Array.isArray(topContent) ? topContent : []).map((content) => (
            <div key={content.id} className="bg-white/5 rounded-xl p-4">
              {content.videoUrl ? (
                <video 
                  src={content.videoUrl} 
                  controls
                  className="w-full h-auto rounded-lg mb-4 bg-black/20"
                  crossOrigin="anonymous"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img 
                  src={content.image} 
                  alt="Top Content" 
                  className="w-full h-auto object-contain rounded-lg mb-4 bg-black/20"
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                />
              )}
              <p className="text-white/80 text-sm mb-4 line-clamp-2">{content.caption}</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/70">Engagement:</span>
                  <span className="text-green-400">%{content.engagement}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Reach:</span>
                  <span className="text-white">{(content.reach ?? 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">{t('socialMedia.common.likes')}</span>
                  <span className="text-white">{(content.likes ?? 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">{t('socialMedia.common.comments')}</span>
                  <span className="text-white">{content.comments}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {modalChart && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setModalChart(null)}></div>
          <div className="absolute top-2 left-2 right-2 bottom-2 sm:top-4 sm:left-4 sm:right-4 sm:bottom-4 md:top-10 md:left-10 md:right-10 md:bottom-10 bg-gray-900 rounded-xl md:rounded-2xl border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b border-white/10">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">
                {modalChart === 'engagement' && t('socialMedia.common.engagementTrend')}
                {modalChart === 'reach' && t('socialMedia.common.reachAnalysis')}
                {modalChart === 'contentPerformance' && t('socialMedia.common.contentPerformanceAnalysis')}
                {modalChart === 'hashtags' && t('socialMedia.common.hashtagAnalysis')}
                {modalChart === 'sentiment' && t('socialMedia.common.sentimentAnalysis')}
              </h3>
              <button
                onClick={() => setModalChart(null)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-white/70 hover:text-white" />
              </button>
            </div>
            <div className="p-3 sm:p-4 md:p-6 h-[calc(100%-60px)] sm:h-[calc(100%-70px)] md:h-[calc(100%-80px)]">
              {modalChart === 'engagement' && <canvas ref={modalEngagementChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'reach' && <canvas ref={modalReachChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'contentPerformance' && <canvas ref={modalContentPerformanceChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'hashtags' && <canvas ref={modalHashtagsChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'sentiment' && <canvas ref={modalSentimentChartRef} className="w-full h-full"></canvas>}
            </div>
          </div>
        </div>
      )}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl font-bold text-white">{t('socialMedia.common.aiAnalysisTitle')}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.startDate') || 'Başlangıç Tarihi'}</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.endDate') || 'Bitiş Tarihi'}</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
              </div>
              <div className="text-white/80">
                {t('socialMedia.common.estimatedCredits')}
                <div className="mt-2 text-2xl font-semibold text-white">
                  {estimateLoading ? t('socialMedia.common.calculating') : (estimatedCredits !== null 
                    ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                    : '—')}
                </div>
              </div>
              <button
                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!startDate || !endDate}
                onClick={async () => {
                  if (!startDate || !endDate) return;
                  setEstimateLoading(true);
                  setEstimatedCredits(null);
                  try {
                    const res = await creditsAPI.estimate('instagram', startDate, endDate);
                    const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                    setEstimatedCredits(typeof value === 'number' ? value : null);
                  } finally {
                    setEstimateLoading(false);
                  }
                }}
              >
                {t('socialMedia.common.calculateCredits') || 'Kredi Hesapla'}
              </button>
              <button
                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                onClick={async () => {
                  try {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    await analysisJobsAPI.start('instagram', startDate, endDate);
                  } finally {
                    setEstimateLoading(false);
                    setAiModalOpen(false);
                  }
                }}
              >
                {t('socialMedia.common.spend')}
              </button>
              <button
                className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                onClick={() => {
                  setAiModalOpen(false);
                  setStartDate('');
                  setEndDate('');
                  setEstimatedCredits(null);
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramPage;
